import { useEffect, useMemo, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import type {
  ApiClient, AussenHydrant, AussenLeerrohr, AussenLeitung,
  AussenNachweisHydrant, AussenNachweisHydranten, AussenSchacht,
  AussenUebersicht, AussenVersickerungsbauwerk,
} from '../api';
import { Fachaktionen } from './Fachaktionen';
import {
  artname, bauartname, gewerkname, herkunftname, istDruckkreis, kreisname,
  nachweisstandname, richtungname,
} from '../aussenKlartext';
import { PageHeader } from '../rahmen/PageHeader';
import { LocalTabs } from '../rahmen/LocalTabs';
import { AussenBerechnung } from './AussenBerechnung';
import { AussenNachweis } from './AussenNachweis';
import { AussenGeraete } from './AussenGeraete';
import { GewerkUnterlagenZip } from '../UnterlagenZipKnopf';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, FilterBar, KpiCard, KpiReihe, LoadingState,
  PropertyGrid, StatusBadge, Vorbehalte, formatiereZahl, useFilterInUrl, type FilterDefinition, type Spalte,
} from '../muster';
import {
  AussenKopfKontext, berichtZiel, bewertungStatus, kfText, sperrenVorbehalte, useAussenKopf,
  useAussenKopfSammlung, useBerichtsstand,
} from './aussenanlagen/kopf';
import './aussenanlagen/aussen.css';

/**
 * Das Gewerk Außenanlagen — ab der Gebäudewand.
 *
 * --- Warum ein eigener Bereich ------------------------------------------------
 *
 * Weil er zwei Gewerke trägt. Im selben Modell liegen 502 Rohre in acht
 * Systemen — darunter Fernwärme, die der Heizung gehört — und daneben 2.646
 * Leerrohre mit 154 Elektroschächten. Derselbe Graben, dieselbe Verlegetiefe.
 *
 * --- Warum die Sperren ganz oben stehen ---------------------------------------
 *
 * Fünfzehn Bauwerke tragen ein Volumen. Die Fläche, aus der es folgen müsste,
 * steht als Text im Typnamen: „Rigole Podium Bereich 1f+A1,A2,A3,50% B6". Das
 * ist eine Notiz des Planers, keine Datenstruktur.
 *
 * Ein Volumen ohne kf-Wert und Einzugsfläche ist nicht bemessen, sondern
 * gesetzt. Eine Übersicht, die das nicht an erster Stelle sagt, lädt dazu ein,
 * es für einen Nachweis zu halten — und dann stehen fünfzehn
 * Versickerungsanlagen ohne Bemessung im Bauantrag.
 *
 * --- TS9b1b (TD3, 26.10.2026): ein Kopf für acht Reiter ----------------------
 *
 * TX-STANDARD S1.2 und Entscheidung E1 (MSR-Muster): genau ein `PageHeader`
 * mit `<h1>` im Rahmen, die Reiter darin. Rechenstand, Exportleiste und
 * Vorbehalte meldet die gewählte Unterseite über `seiten/aussenanlagen/kopf.tsx`;
 * die Sperren stehen als Vorbehalte im Kasten unter dem Kopf, die Kennzahlen
 * als `KpiReihe`. Alle Listen laufen über `DataTable` (S1.3) - ohne die
 * frühere Kappung auf 200 Schächte und ohne Server-Blättern: Leitungen und
 * Leerrohre kommen ganz (Seiten zu 500 parallel, Entscheidung E2: gemessen
 * 493 KB / 62 ms bzw. 1,97 MB / 365 ms am Projekt 8000).
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

/*
 * KREISNAMEN und BAUARTEN standen bis zum 23.09.2026 hier als zwei lokale
 * Teillisten - sieben Kreise und sieben Bauarten, waehrend die Datenbank
 * mehr fuehrt. Sie stehen jetzt vollstaendig in `../aussenKlartext`,
 * gespiegelt aus `AussenKlartext.cs`, und ein unbekannter Schluessel faellt
 * dort auf den lesbaren Rueckfall statt auf den rohen Schluessel.
 */

/** Kontext, den `AussenanlagenRahmen` per `Outlet` an jede Unterseite weiterreicht. */
export interface AussenanlagenKontext {
  api: ApiClient;
  projektId: string;
  u: AussenUebersicht;
}

/**
 * Der Rahmen um die acht Außenanlagen-Seiten (Paket N2, Muster N1) —
 * Reiter als echte Routen statt `useState('bestand')`.
 *
 * Kopf, Sperren, Fachaktionen und Kennzahlen gelten für ALLE acht
 * Unterseiten gleich und stehen deshalb hier oben; der Reiterinhalt steht in
 * eigenen Routen mit `Outlet`. `AussenBerechnung` und `AussenNachweis` sind
 * self-fetching (`api`+`projektId`) und melden ihren Rechenstand an den Kopf.
 */
export function AussenanlagenRahmen() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [u, setU] = useState<AussenUebersicht | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const { melden, kopf } = useAussenKopfSammlung();

  const laden = () => {
    if (!projekt) return;

    setFehler(null);
    api.aussenUebersicht(projekt.id)
      .then(setU)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  useEffect(laden, [api, projekt?.id]);

  if (!projekt) return <LoadingState text="Projekt wird abgerufen …" />;

  const bestand = u !== null && u.gesamt > 0;

  return (
    <>
      <PageHeader
        titel="Außenanlagen"
        beschreibung={'Erschließung ab der Gebäudewand: Leitungen, Leerrohre, Schächte, Hydranten und '
          + 'Versickerung — Bestand, Berechnungen und Nachweis.'}
        meta={kopf.meta}
        aktionen={kopf.ziele.length > 0 ? <ExportLeiste ziele={kopf.ziele} /> : undefined}
      >
        {bestand && (
          <LocalTabs
            ariaLabel="Außenanlagen"
            eintraege={[
              { to: '.', beschriftung: 'Bestand', end: true },
              { to: 'sanitaer', beschriftung: 'Sanitär' },
              { to: 'elektro', beschriftung: 'Elektro' },
              { to: 'hydranten', beschriftung: 'Hydranten' },
              { to: 'geraete', beschriftung: 'Geräte' },
              { to: 'versickerung', beschriftung: 'Versickerung' },
              { to: 'berechnung', beschriftung: 'Berechnungen' },
              // TS9b2: die Rückhaltung je Einleitstelle (DWA-A 117) - sie steht im Versickerungslauf.
              { to: 'rueckhaltung', beschriftung: 'Rückhaltung' },
              // Der Nachweis steht NACH den Berechnungen und nicht davor:
              // Er zeigt den abgelegten Lauf, und ohne Lauf ist er leer.
              { to: 'nachweis', beschriftung: 'Nachweis' },
            ]}
          />
        )}
      </PageHeader>

      <Vorbehalte eintraege={[...sperrenVorbehalte(u?.sperren ?? []), ...kopf.vorbehalte]} />

      {/* Die Auftragsart 90 — ohne diesen Knopf gibt es keinen Bestand. */}
      <Fachaktionen
        api={api}
        rolleKey="aussenanlagen"
        leseArt={90}
        leseText="Modell lesen"
      >
        <GewerkUnterlagenZip api={api} projektId={projekt.id} gewerk="aussen" />
      </Fachaktionen>

      {fehler ? (
        <ErrorState
          titel="Die Übersicht der Außenanlagen ließ sich nicht laden."
          meldung="Ohne Übersicht stehen weder Kennzahlen noch Reiter da - das ist ein Fehler und nicht der Bestand."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      ) : !u ? (
        <LoadingState text="Übersicht der Außenanlagen wird abgerufen …" />
      ) : !bestand ? (
        <EmptyState
          titel="Noch kein Außenanlagenbestand."
          erklaerung={(
            <>
              Er entsteht aus einem Auftrag mit der Außenanlagenaufnahme — oben
              „Modell lesen“. Der Lauf liest <em>zwei</em> Netze aus demselben
              Modell: die Erschließungsleitungen ab Gebäudewand und das
              Leerrohrnetz. Sie werden über die Anschlussdomäne getrennt — wer sie
              zusammenwirft, verbindet eine Schmutzwasserleitung mit einem
              Leerrohr. Erwartet werden rund 502 Rohre in acht Systemen, 2.646
              Leerrohre, 1.686 Leerrohrformteile, 169 Schächte Sanitär, 154
              Schächte Elektro, 15 Versickerungsbauwerke und 11
              Überflurhydranten. Der Auftrag muss auf dem
              {' '}<strong>Außenanlagenmodell</strong> laufen — nicht auf einem
              Gebäudemodell.
            </>
          )}
        />
      ) : (
        <>
          <Kennzahlen u={u} />

          <AussenKopfKontext.Provider value={melden}>
            <Outlet context={{ api, projektId: projekt.id, u } satisfies AussenanlagenKontext} />
          </AussenKopfKontext.Provider>
        </>
      )}
    </>
  );
}

export function AussenBestandSeite() {
  const { u } = useOutletContext<AussenanlagenKontext>();
  return <Bestand u={u} />;
}

export function AussenSanitaerSeite() {
  const { api, projektId } = useOutletContext<AussenanlagenKontext>();
  return <Sanitaerteil api={api} projektId={projektId} />;
}

export function AussenElektroSeite() {
  const { api, projektId } = useOutletContext<AussenanlagenKontext>();
  return <Elektroteil api={api} projektId={projektId} />;
}

export function AussenHydrantenSeite() {
  const { api, projektId } = useOutletContext<AussenanlagenKontext>();
  return <Hydranten api={api} projektId={projektId} />;
}

export function AussenGeraeteSeite() {
  const { api, projektId } = useOutletContext<AussenanlagenKontext>();
  return <AussenGeraete api={api} projektId={projektId} />;
}

export function AussenVersickerungSeite() {
  const { api, projektId } = useOutletContext<AussenanlagenKontext>();
  return <Versickerung api={api} projektId={projektId} />;
}

export function AussenBerechnungSeite() {
  const { api, projektId } = useOutletContext<AussenanlagenKontext>();
  return <AussenBerechnung api={api} projektId={projektId} />;
}

export function AussenNachweisSeite() {
  const { api, projektId } = useOutletContext<AussenanlagenKontext>();
  return <AussenNachweis api={api} projektId={projektId} />;
}

// ═══════════════════════════════════════════════════════════════════════════
// Abruf
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Daten einer Unterseite: `daten` ist `undefined`, solange der Abruf läuft; ein Fehler steht als Text da und
 * lässt sich erneut versuchen (S1.4). Bis TS9b1b fiel ein Fehler hier still auf eine leere Liste - und eine leere
 * Liste sieht aus wie ein leerer Bestand.
 */
function useAbruf<T>(holen: () => Promise<T>, abhaengig: readonly unknown[]) {
  const [daten, setDaten] = useState<T | undefined>(undefined);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);

  useEffect(() => {
    let lebt = true;
    setDaten(undefined);
    setFehler(null);
    holen()
      .then((x) => { if (lebt) setDaten(x); })
      .catch((e) => { if (lebt) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { lebt = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...abhaengig, versuch]);

  return {
    daten,
    fehler,
    laedt: daten === undefined && fehler === null,
    erneut: () => setVersuch((v) => v + 1),
  };
}

/** So viele Zeilen nimmt der Server je Seite höchstens an (er klemmt `proSeite` auf 10 bis 500). */
const PRO_SEITE = 500;

/**
 * Alle Seiten einer blätternden Liste (Entscheidung E2): Seite 1 nennt die Gesamtzahl, die übrigen Seiten kommen
 * gleichzeitig. Die Tabelle sucht, sortiert und filtert dann über ALLE Zeilen - eine Suche über die angezeigte
 * Seite fände nur, was zufällig auf ihr steht.
 */
async function alleSeiten<A extends { gesamt: number; proSeite: number; zeilen: unknown[] }>(
  hole: (seite: number) => Promise<A>,
): Promise<{ erste: A; zeilen: A['zeilen'] }> {
  const erste = await hole(1);
  const je = erste.proSeite > 0 ? erste.proSeite : PRO_SEITE;
  const seiten = Math.ceil(erste.gesamt / je);
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, seiten - 1) }, (_, i) => hole(i + 2)));
  const zeilen: unknown[] = [...erste.zeilen];
  for (const r of rest) zeilen.push(...r.zeilen);
  return { erste, zeilen: zeilen as A['zeilen'] };
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Zahlen über den Reitern
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Zahlen über den Reitern.
 *
 * Ausserhalb der Reiter, weil sie fuer jeden gelten. Die Auswahl folgt der
 * Kette, an der die Aussenentwaesserung haengt:
 *
 *   Ohne Kreis wird eine Leitung von keiner Bemessung erfasst.
 *   Ohne Anschluss ist ein Schacht kein Knoten - und ohne Knoten gibt es kein
 *   Netz, also keinen Zulauf und keinen Fuellungsgrad.
 *
 * Genau die zweite Zahl fehlte bisher auf jedem Blatt: Am Projekt 8000 haben
 * 25 von 348 Schaechten einen Anschluss, und der hydraulische Nachweis ist
 * damit keine Fleissaufgabe an ein paar Zeilen, sondern eine an 323
 * Schaechten.
 *
 * Fünf Karten (S1.2 Nr. 3: höchstens sechs). Eine Bewertung tragen nur die
 * beiden, an denen eine Grenze geprüft wird: „ohne Kreis“ gegen null und die
 * Anschlüsse gegen die Zahl der Schächte (dort nur die Warnung: mindestens so
 * viele Anschlüsse wie Schächte heißt nicht, dass jeder Schacht einen hat).
 */
function Kennzahlen({ u }: { u: AussenUebersicht }) {
  const z = useMemo(() => {
    const leitungen = u.leitungen.reduce((s, l) => s + l.stueck, 0);
    const laenge = u.leitungen.reduce((s, l) => s + l.laengeM, 0);

    const ohneKreis = u.leitungen
      .filter((l) => !l.kreis)
      .reduce((s, l) => s + l.stueck, 0);

    const bergauf = u.leitungen.reduce((s, l) => s + l.bergauf, 0);

    const schaechte = u.schaechte.reduce((s, x) => s + x.stueck, 0);
    const anschluesse = u.anschluesse.reduce((s, x) => s + x.stueck, 0);

    const ohneRichtung = u.anschluesse
      .filter((x) => !x.richtung || x.richtung === 'unbekannt')
      .reduce((s, x) => s + x.stueck, 0);

    return { leitungen, laenge, ohneKreis, bergauf, schaechte, anschluesse,
             ohneRichtung };
  }, [u]);

  return (
    <KpiReihe>
      <KpiCard titel="Leitungen" wert={z.leitungen} vergleich={`${formatiereZahl(z.laenge, 0)} m`} />
      <KpiCard
        titel="ohne Kreis" wert={z.ohneKreis}
        zustand={z.ohneKreis > 0 ? 'warnung' : 'gut'}
        zustandText={z.ohneKreis > 0 ? 'von keiner Bemessung erfasst' : 'alle zugeordnet'}
      />
      <KpiCard titel="Schächte" wert={z.schaechte} />
      <KpiCard
        titel="Anschlüsse" wert={z.anschluesse}
        vergleich={z.ohneRichtung > 0 ? `${formatiereZahl(z.ohneRichtung, 0)} ohne Richtung` : undefined}
        zustand={z.anschluesse < z.schaechte ? 'warnung' : undefined}
        zustandText={z.anschluesse < z.schaechte ? 'weniger als Schächte' : undefined}
      />
      <KpiCard titel="bergauf" wert={z.bergauf} vergleich="Kurvenrichtung, kein Mangel" />
    </KpiReihe>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Der Bestand
// ═══════════════════════════════════════════════════════════════════════════

type Leitungsgruppe = AussenUebersicht['leitungen'][number];
type Leerrohrgruppe = AussenUebersicht['leerrohre'][number];
type Schachtgruppe = AussenUebersicht['schaechte'][number];

const BESTAND_LEITUNGEN: Spalte<Leitungsgruppe>[] = [
  {
    schluessel: 'systemtyp', titel: 'Systemtyp', immerSichtbar: true, wert: (l) => l.systemtyp,
    inhalt: (l) => l.systemtyp ?? <em className="schwach">ohne</em>,
  },
  {
    schluessel: 'kreis', titel: 'Kreis', art: 'status',
    wert: (l) => (l.kreis ? kreisname(l.kreis) : 'keine Kreisregel'),
    inhalt: (l) => (l.kreis ? kreisname(l.kreis) : <StatusBadge zustand="fehler" text="keine Kreisregel" klein />),
  },
  { schluessel: 'stueck', titel: 'Bauteile', art: 'zahl', nachkomma: 0, fuss: 'summe' },
  { schluessel: 'rohre', titel: 'davon Rohre', art: 'zahl', nachkomma: 0, fuss: 'summe' },
  { schluessel: 'laengeM', titel: 'Länge', einheit: 'm', art: 'zahl', nachkomma: 1, fuss: 'summe' },
  { schluessel: 'mitGefaelle', titel: 'mit Gefälle', art: 'zahl', nachkomma: 0, fuss: 'summe' },
  {
    schluessel: 'bergauf', titel: 'negativ', art: 'zahl', nachkomma: 0, fuss: 'summe',
    // Null steht als Strich, zählt im Fuß aber als Wert: Keine negative
    // Neigung ist ein Befund über die Leitung, keine Fehlstelle.
    titelHinweis: 'Kurvenrichtung, kein Befund',
    inhalt: (l) => <span className="schwach">{l.bergauf > 0 ? formatiereZahl(l.bergauf, 0) : '—'}</span>,
  },
];

const BESTAND_LEERROHRE: Spalte<Leerrohrgruppe>[] = [
  { schluessel: 'art', titel: 'Art', immerSichtbar: true, wert: (l) => artname(l.art) },
  { schluessel: 'stueck', titel: 'Stück', art: 'zahl', nachkomma: 0, fuss: 'summe' },
  { schluessel: 'laengeM', titel: 'Länge', einheit: 'm', art: 'zahl', nachkomma: 1, fuss: 'summe' },
  { schluessel: 'mitNennweite', titel: 'mit Nennweite', art: 'zahl', nachkomma: 0, fuss: 'summe' },
  { schluessel: 'mitTrasse', titel: 'mit Trasse', art: 'zahl', nachkomma: 0, fuss: 'summe' },
];

const BESTAND_SCHAECHTE: Spalte<Schachtgruppe>[] = [
  { schluessel: 'gewerk', titel: 'Gewerk', immerSichtbar: true, wert: (s) => gewerkname(s.gewerk) },
  {
    schluessel: 'bauart', titel: 'Bauart', wert: (s) => (s.bauart ? bauartname(s.bauart) : null),
    inhalt: (s) => (s.bauart ? bauartname(s.bauart) : <em className="schwach">ohne</em>),
  },
  { schluessel: 'stueck', titel: 'Stück', art: 'zahl', nachkomma: 0, fuss: 'summe' },
  { schluessel: 'mitSystem', titel: 'mit Systemnamen', art: 'zahl', nachkomma: 0, fuss: 'summe' },
];

/**
 * Warum Elektro und Sanitär getrennte Reiter/Routen bleiben (Paket N2,
 * Muster N1: die Gliederung steht in `seiten/aussenanlagen/abschnitte.ts`,
 * die frühere Knopfzeile `Abschnitte` ist entfallen).
 *
 * Außen liegen beide Gewerke im selben Graben, und in einer gemeinsamen
 * Liste stehen 1.271 Leerrohrtrassen neben 349 Schächten. Wer die
 * Entwässerung prüft, blättert dann durch Kabelwege. Getrennte Reiter filtern
 * nach Gewerk, statt eine Spalte „Gewerk“ zum Selbersuchen anzubieten.
 */
function Bestand({ u }: { u: AussenUebersicht }) {
  return (
    <>
      <ContentSection
        titel="Erschließungsleitungen"
        beschreibung={'Acht Gewerke ab Gebäudewand, im Systemtyp sauber getrennt. Das Gefälle wird aus den '
          + 'Anschlusshöhen gerechnet und nicht aus einem Parameter gelesen.'}
      >
        <p className="aus-hinweis">
          Die Spalte <em>negativ</em> ist <strong>kein</strong> Befund: Revit
          vergibt die Richtung einer Kurve beliebig, und bei Regen- und
          Schmutzwasser sind 47 % der Gefälle negativ. Wäre das ein
          Modellfehler, müsste knapp die Hälfte aller Grundleitungen bergauf
          laufen. Geprüft wird deshalb die <strong>Neigung</strong>; ob eine
          Leitung in die richtige Richtung fällt, sagt erst das Netz.
        </p>

        <DataTable<Leitungsgruppe>
          name="Leitungen je Systemtyp und Kreis"
          spalten={BESTAND_LEITUNGEN}
          zeilen={u.leitungen}
          schluessel={(l) => `${l.systemtyp ?? ''}|${l.kreis ?? ''}`}
          zeilenKlasse={(l) => (l.kreis ? undefined : 'aus-zeile-fehler')}
          leer={<EmptyState klein titel="Keine Erschließungsleitung im Bestand." />}
          ausfuhr
          ausfuhrDateiname="aussen-bestand-leitungen"
          dicht
        />
      </ContentSection>

      <ContentSection
        titel="Leerrohrnetz"
        beschreibung={'Alle vom Typ Conduit_Outdoor — der Typ unterscheidet nichts: keine Nennweite, keine '
          + 'Belegung, kein Gewerk. Die Zuordnung muss aus der Trasse über die Schächte folgen.'}
      >
        <DataTable<Leerrohrgruppe>
          name="Leerrohre je Art"
          spalten={BESTAND_LEERROHRE}
          zeilen={u.leerrohre}
          schluessel={(l) => l.art}
          leer={<EmptyState klein titel="Kein Leerrohr im Bestand." />}
          dicht
        />
      </ContentSection>

      <ContentSection titel="Schächte und Hydranten">
        <DataTable<Schachtgruppe>
          name="Schächte je Gewerk und Bauart"
          spalten={BESTAND_SCHAECHTE}
          zeilen={u.schaechte}
          schluessel={(s) => `${s.gewerk}|${s.bauart ?? ''}`}
          leer={<EmptyState klein titel="Kein Schacht im Bestand." />}
          dicht
        />

        <PropertyGrid
          spalten={3}
          dicht
          eintraege={[
            ...u.anschluesse.map((a) => ({
              beschriftung: `Anschlüsse · ${richtungname(a.richtung)}`, wert: a.stueck, nachkomma: 0,
            })),
            { beschriftung: 'Überflurhydranten', wert: u.hydranten, nachkomma: 0 },
          ]}
        />
      </ContentSection>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sanitär und Elektro
// ═══════════════════════════════════════════════════════════════════════════

/** Ein Anschluss eines Schachts: gefunden (mit Fließrichtung) oder ohne Leitung - das ist ein Befund. */
function Anschlussmarke({ a }: { a: AussenSchacht['anschluesse'][number] }) {
  const pfeil = a.richtung === 'ablauf' ? ' ↓' : a.richtung === 'zulauf' ? ' ↑' : '';
  return a.leitungId
    ? <StatusBadge zustand="gut" text={`${a.system}${pfeil}`} klein
        titel={`${richtungname(a.richtung)}, DN ${a.nennweite ?? '?'}`} />
    : <StatusBadge zustand="fehler" text={a.system} klein titel="keine Leitung zu diesem System gefunden" />;
}

const SCHACHTSPALTEN: Spalte<AussenSchacht>[] = [
  { schluessel: 'element', titel: 'Element', immerSichtbar: true, wert: (s) => String(s.revitElementId) },
  { schluessel: 'gewerk', titel: 'Gewerk', wert: (s) => gewerkname(s.gewerk) },
  { schluessel: 'typ', titel: 'Typ', wert: (s) => s.typ },
  { schluessel: 'nennweite', titel: 'DN', art: 'zahl', nachkomma: 0 },
  {
    schluessel: 'systemnameRoh', titel: 'Systemname im Modell',
    inhalt: (s) => <span className="schwach">{s.systemnameRoh ?? '—'}</span>,
  },
  {
    schluessel: 'anschluesse', titel: 'Anschlüsse', art: 'status',
    wert: (s) => s.anschluesse.map((a) => `${a.system}${a.leitungId ? '' : ' ohne Leitung'}`).join(', '),
    inhalt: (s) => (s.anschluesse.length === 0
      ? <em className="schwach">keine</em>
      : <span className="aus-reihe">{s.anschluesse.map((a) => <Anschlussmarke key={a.system} a={a} />)}</span>),
  },
];

/**
 * Die Schächte EINES Gewerks.
 *
 * Das Gewerk kommt von außen und nicht aus einem Auswahlfeld: Sanitär und
 * Elektro haben eigene Reiter, und ein Filter im Reiter, der ohnehin schon
 * filtert, ist eine Falle — man stellt ihn einmal um und wundert sich später
 * über die Zahlen. Alle Schächte stehen in der Tabelle; die frühere Kappung
 * auf 200 Zeilen entfällt (S1.3), `DataTable` zeichnet ab 200 nur die
 * sichtbaren.
 */
function Schaechte({ api, projektId, gewerk }: {
  api: ApiClient; projektId: string; gewerk: string;
}) {
  const abruf = useAbruf(() => api.aussenSchaechte(projektId, gewerk || undefined), [api, projektId, gewerk]);

  return (
    <ContentSection
      titel="Schächte"
      beschreibung={'Ein Schacht ist ein Knoten, kein Bauteil mit einem System: Im Modell steht an ihm '
        + '„SP 36,SP 35“. An jedem kommen Teilströme zusammen, und der Abfluss unten ist die Summe der Zuläufe '
        + 'oben. Ein Anschluss ohne Leitung ist ein Befund — entweder fehlt die Leitung, oder der Systemname ist '
        + 'veraltet.'}
    >
      <DataTable<AussenSchacht>
        name={`Schächte ${gewerkname(gewerk)}`}
        spalten={SCHACHTSPALTEN}
        zeilen={abruf.daten ?? []}
        schluessel={(s) => s.id}
        laedt={abruf.laedt}
        fehler={abruf.fehler ?? undefined}
        beiErneutVersuchen={abruf.erneut}
        leer={<EmptyState klein titel={`Kein Schacht im Gewerk ${gewerkname(gewerk)}.`}
          erklaerung="Schächte entstehen mit der Außenanlagenaufnahme („Modell lesen“)." />}
        ausfuhr
        ausfuhrDateiname={`aussen-schaechte-${gewerk}`}
        dicht
      />
    </ContentSection>
  );
}

/**
 * Der Sanitärteil: Leitungen und Schächte.
 *
 * Beides auf einer Seite, weil beides ein Netz ist. Ein Schacht ohne seine
 * Leitungen ist ein Punkt auf der Karte, eine Leitung ohne ihre Schächte ein
 * Strich ohne Anfang.
 */
function Sanitaerteil({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  return (
    <>
      <Leitungen api={api} projektId={projektId} />
      <Schaechte api={api} projektId={projektId} gewerk="sanitaer" />
    </>
  );
}

/** Der Elektroteil: Leerrohre und Schächte. */
function Elektroteil({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  return (
    <>
      <Leerrohre api={api} projektId={projektId} />
      <Schaechte api={api} projektId={projektId} gewerk="elektro" />
    </>
  );
}

// Die FILTERWERTE sind die rohen Schluessel. Die Beschriftung kommt aus dem
// Woerterbuch: „alle Kreise" ist als Filtertext richtig, `trinkwasser` in
// einer Zelle nicht.
const KREISE = [
  'regenwasser', 'schmutzwasser', 'trinkwasser', 'loeschwasser',
  'kondensat', 'fernwaerme_vorlauf', 'fernwaerme_ruecklauf',
] as const;

const LEITUNGSFILTER: FilterDefinition[] = [{
  schluessel: 'kreis', beschriftung: 'Kreis', art: 'auswahl', platzhalter: 'alle Kreise',
  optionen: KREISE.map((k) => ({ wert: k, beschriftung: kreisname(k) })),
}];

/**
 * Das Gefaelle ist gerechnet, wenn es eine HERKUNFT hat - an den Formteilen
 * steht keine, und dort ist auch kein Wert. Eine 0,0 ohne Herkunft waere
 * keine Neigung, sondern ein Vorgabewert.
 */
const gefaelleGerechnet = (l: AussenLeitung) => l.gefaelle != null && l.gefaelleHerkunft != null;

const LEITUNGSSPALTEN: Spalte<AussenLeitung>[] = [
  { schluessel: 'element', titel: 'Element', immerSichtbar: true, wert: (l) => String(l.revitElementId) },
  {
    schluessel: 'kreis', titel: 'Kreis', art: 'status',
    wert: (l) => (l.kreis ? kreisname(l.kreis) : 'keine Kreisregel'),
    inhalt: (l) => (l.kreis
      ? <span title={l.kreisHerkunft ? `Zuordnung: ${herkunftname(l.kreisHerkunft)}` : undefined}>{kreisname(l.kreis)}</span>
      : <StatusBadge zustand="fehler" text="keine Kreisregel" klein />),
  },
  { schluessel: 'art', titel: 'Art', wert: (l) => artname(l.art) },
  {
    schluessel: 'typ', titel: 'Typ im Modell', titelHinweis: 'Typname aus dem Revit-Modell — kein Fabrikat',
    wert: (l) => l.typ,
  },
  {
    schluessel: 'nennweite', titel: 'DN', art: 'zahl', nachkomma: 0,
    inhalt: (l) => (
      <span title={l.nennweite == null
        ? 'Für dieses Bauteil ist keine Nennweite abgeleitet.'
        : `Nennweite: ${herkunftname(l.nennweiteHerkunft)}`}>
        {formatiereZahl(l.nennweite, 0)}
      </span>
    ),
  },
  { schluessel: 'innendurchmesser', titel: 'd_i', einheit: 'mm', art: 'zahl', nachkomma: 1 },
  {
    schluessel: 'laenge', titel: 'L', einheit: 'm', art: 'zahl', nachkomma: 2,
    wert: (l) => (l.laengeMm == null ? null : l.laengeMm / 1000), fuss: { art: 'summe', schranke: true },
  },
  {
    schluessel: 'gefaelle', titel: 'J', einheit: '‰', art: 'zahl', nachkomma: 1,
    wert: (l) => (gefaelleGerechnet(l) ? l.gefaelle! * 1000 : null),
    inhalt: (l) => {
      const druck = istDruckkreis(l.kreis);
      return (
        <span
          className={druck || !gefaelleGerechnet(l) ? 'schwach' : undefined}
          title={!gefaelleGerechnet(l)
            ? 'Kein Gefälle gerechnet — dieses Bauteil trägt keine zwei Anschlusshöhen.'
            : druck
              ? 'Neigung aus der Geometrie. Für eine Druckleitung ist sie kein Bemessungswert.'
              : `Neigung: ${herkunftname(l.gefaelleHerkunft)}`}
        >
          {gefaelleGerechnet(l) ? formatiereZahl(l.gefaelle! * 1000, 1) : '—'}
        </span>
      );
    },
  },
  {
    schluessel: 'mindestgefaelle', titel: 'Mindestgefälle', art: 'status',
    wert: (l) => (l.mindestgefaelleOk === null ? null : l.mindestgefaelleOk ? 'erfüllt' : 'unterschritten'),
    inhalt: (l) => {
      if (l.mindestgefaelleOk === null) {
        return (
          <span className="schwach aus-fein">
            {istDruckkreis(l.kreis) ? 'nicht zu prüfen (Druckleitung)' : 'nicht gerechnet'}
          </span>
        );
      }
      const soll = `Soll ${formatiereZahl(l.mindestgefaelleSoll, 1)} ‰`;
      return l.mindestgefaelleOk
        ? <StatusBadge zustand="gut" text="erfüllt" titel={soll} klein />
        : <StatusBadge zustand="warnung" text="unterschritten" titel={soll} klein />;
    },
  },
];

/**
 * Die Sanitärleitungen, nach Kreis filterbar.
 *
 * Der Kreis und nicht das Gewerk ist hier die nützliche Unterscheidung:
 * Regen- und Schmutzwasser liegen im selben Graben, werden aber getrennt
 * gerechnet — das eine im Freispiegel, das andere gegen die Rückstauebene.
 *
 * Alle Leitungen kommen auf einmal (E2); der Kreisfilter wirkt in der Seite
 * und steht in der Adresse. Der Bericht dieses Bereichs (Freispiegel) steht
 * in der Exportleiste des Kopfs.
 */
function Leitungen({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const abruf = useAbruf(
    () => alleSeiten((s) => api.aussenLeitungen(projektId, undefined, s, PRO_SEITE)), [api, projektId]);
  const [filter, setFilter] = useFilterInUrl(LEITUNGSFILTER);
  const kreis = typeof filter.kreis === 'string' ? filter.kreis : '';
  const alle = abruf.daten?.zeilen ?? [];
  const zeilen = kreis ? alle.filter((l) => l.kreis === kreis) : alle;

  const stand = useBerichtsstand(api, projektId, 'freispiegel');
  useAussenKopf({ ziele: [berichtZiel(api, projektId, 'freispiegel', stand)] }, [api, projektId, stand]);

  return (
    <ContentSection
      titel="Leitungen"
      beschreibung="Jede Erschließungsleitung mit Kreis, Nennweite, Länge und Neigung; das Mindestgefälle nur an Freispiegelleitungen."
      werkzeuge={<FilterBar filter={LEITUNGSFILTER} werte={filter} beiAenderung={setFilter} />}
    >
      <DataTable<AussenLeitung>
        name="Leitungen"
        spalten={LEITUNGSSPALTEN}
        zeilen={zeilen}
        schluessel={(l) => l.id}
        laedt={abruf.laedt}
        fehler={abruf.fehler ?? undefined}
        beiErneutVersuchen={abruf.erneut}
        leer={kreis
          ? <EmptyState klein titel={`Keine Leitung im Kreis „${kreisname(kreis)}“.`}
              erklaerung="Den Kreisfilter zurücksetzen, um alle Leitungen zu sehen." />
          : <EmptyState klein titel="Keine Erschließungsleitung aufgenommen."
              erklaerung="Leitungen entstehen mit der Außenanlagenaufnahme („Modell lesen“)." />}
        ausfuhr
        ausfuhrDateiname="aussen-leitungen"
        dicht
      />
    </ContentSection>
  );
}

const LEERROHRFILTER: FilterDefinition[] = [
  { schluessel: 'ohneTrasse', beschriftung: 'nur Rohre ohne Trasse', art: 'haken' },
];

const LEERROHRSPALTEN: Spalte<AussenLeerrohr>[] = [
  {
    schluessel: 'element', titel: 'Element', immerSichtbar: true,
    wert: (r) => (r.revitElementId == null ? null : String(r.revitElementId)),
  },
  { schluessel: 'typ', titel: 'Typ', wert: (r) => r.typ },
  { schluessel: 'nennweite', titel: 'DN', art: 'zahl', nachkomma: 0 },
  { schluessel: 'innendurchmesserMm', titel: 'd_i', einheit: 'mm', art: 'zahl', nachkomma: 1 },
  {
    schluessel: 'laenge', titel: 'L', einheit: 'm', art: 'zahl', nachkomma: 2,
    wert: (r) => (r.laengeMm == null ? null : r.laengeMm / 1000), fuss: { art: 'summe', schranke: true },
  },
  { schluessel: 'kabelAnzahl', titel: 'Kabel', art: 'zahl', nachkomma: 0 },
  { schluessel: 'belegungsgradProzent', titel: 'Füllgrad', einheit: '%', art: 'zahl', nachkomma: 0 },
  {
    schluessel: 'trasse', titel: 'Trasse', art: 'status',
    wert: (r) => (r.trasseId ? 'mit Trasse' : 'ohne Trasse'),
    inhalt: (r) => (r.trasseId
      ? <StatusBadge zustand="gut" text="mit Trasse" klein />
      : <StatusBadge zustand="warnung" text="ohne Trasse" klein />),
  },
];

/**
 * Die Leerrohre.
 *
 * Die Kopfzahlen gelten für ALLE Zeilen, nicht für eine Auswahl — eine
 * Gesamtlänge, die sich beim Filtern ändert, wäre keine. Sie stehen rechts am
 * Abschnitt (vom Server); der Summenfuß der Tabelle gilt für die gezeigten
 * Zeilen und sagt „≥“, wenn einer Zeile die Länge fehlt.
 */
function Leerrohre({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const abruf = useAbruf(
    () => alleSeiten((s) => api.aussenLeerrohre(projektId, s, PRO_SEITE)), [api, projektId]);
  const [filter, setFilter] = useFilterInUrl(LEERROHRFILTER);
  const nurOffen = filter.ohneTrasse === true;
  const alle = abruf.daten?.zeilen ?? [];
  const zeilen = nurOffen ? alle.filter((r) => !r.trasseId) : alle;
  const erste = abruf.daten?.erste;

  const stand = useBerichtsstand(api, projektId, 'leerrohr');
  useAussenKopf({ ziele: [berichtZiel(api, projektId, 'leerrohr', stand)] }, [api, projektId, stand]);

  return (
    <ContentSection
      titel="Leerrohre"
      beschreibung={'Der Belegungsgrad folgt DIN VDE 0100-520: 53 % bei einem Kabel, 31 % bei zweien, 40 % ab '
        + 'dreien. Ein Rohr ohne Trasse hängt im Netz nicht — das ist ein Befund und keine Spielart der Anzeige.'}
      meta={erste && (
        <span data-leerrohrsumme>
          Gesamtlänge {formatiereZahl(erste.laengeM, 0)} m · {formatiereZahl(erste.ohneTrasse, 0)} ohne Trasse
        </span>
      )}
      werkzeuge={<FilterBar filter={LEERROHRFILTER} werte={filter} beiAenderung={setFilter} />}
    >
      <DataTable<AussenLeerrohr>
        name="Leerrohre"
        spalten={LEERROHRSPALTEN}
        zeilen={zeilen}
        schluessel={(r) => r.id}
        laedt={abruf.laedt}
        fehler={abruf.fehler ?? undefined}
        beiErneutVersuchen={abruf.erneut}
        leer={nurOffen
          ? <EmptyState klein titel="Jedes Leerrohr hat eine Trasse." />
          : <EmptyState klein titel="Kein Leerrohr aufgenommen."
              erklaerung="Leerrohre entstehen mit der Außenanlagenaufnahme („Modell lesen“)." />}
        ausfuhr
        ausfuhrDateiname="aussen-leerrohre"
        dicht
      />
    </ContentSection>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Hydranten
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Hydrantenzeile mit dem, was der Löschwasserlauf zu ihr weiß. */
interface Hydrantenzeile {
  h: AussenHydrant;
  n: AussenNachweisHydrant | undefined;
  abstand: number | null;
  massgebend: boolean;
}

const HYDRANTENSPALTEN: Spalte<Hydrantenzeile>[] = [
  {
    schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true,
    wert: ({ h, n }) => h.kennung ?? n?.bezeichnung ?? `Element ${h.revitElementId}`,
    inhalt: ({ h, n }) => (h.kennung || n?.bezeichnung
      ? <strong>{h.kennung ?? n?.bezeichnung}</strong>
      : (
        <span className="schwach"
          title="Der Hydrant trägt keine fachliche Kennung. Gezeigt wird die Revit-Elementkennung - sie ist eine Modellnummer und keine Anlagenkennzeichnung.">
          Element&nbsp;{h.revitElementId}
        </span>
      )),
  },
  { schluessel: 'element', titel: 'Element', wert: ({ h }) => (h.revitElementId == null ? null : String(h.revitElementId)) },
  {
    schluessel: 'bauart', titel: 'Bauart', wert: ({ h }) => bauartname(h.bauart),
    inhalt: ({ h }) => <span title={h.typ ?? undefined}>{bauartname(h.bauart)}</span>,
  },
  { schluessel: 'dn', titel: 'DN', wert: ({ h }) => h.nennweite },
  { schluessel: 'abgabe', titel: 'Abgabe', einheit: 'l/min', art: 'zahl', nachkomma: 0, wert: ({ h }) => h.abgabeLMin },
  { schluessel: 'netzdruck', titel: 'Netzdruck', einheit: 'bar', art: 'zahl', nachkomma: 2, wert: ({ h }) => h.netzdruckBar },
  {
    schluessel: 'fliessdruck', titel: 'Fließdruck', einheit: 'bar', art: 'zahl', nachkomma: 2,
    wert: ({ h }) => h.fliessdruckBar,
  },
  { schluessel: 'abstand', titel: 'Nachbarabstand', einheit: 'm', art: 'zahl', nachkomma: 1, wert: (z) => z.abstand },
  {
    schluessel: 'bewertung', titel: 'Bewertung', art: 'status',
    wert: ({ n }) => (n?.bewertung ? bewertungStatus(n.bewertung).text : null),
    inhalt: ({ n }) => (n?.bewertung
      ? <StatusBadge {...bewertungStatus(n.bewertung)} klein />
      : <span className="schwach aus-fein">nicht gerechnet</span>),
  },
  {
    // Der Befund der RECHNUNG hat Vorrang vor dem der AUFNAHME: Er ist der
    // jüngere und der fachliche.
    schluessel: 'befund', titel: 'Befund',
    wert: ({ h, n }) => n?.befund ?? h.befund,
    inhalt: ({ h, n }) => {
      const befund = n?.befund ?? h.befund;
      if (befund) return <span className="aus-fein">{befund}</span>;
      return <span className="schwach">{n?.bewertung === 'ok' ? 'ohne Beanstandung' : '—'}</span>;
    },
  },
  {
    schluessel: 'massgebend', titel: 'maßgebend', art: 'status',
    wert: (z) => (z.massgebend ? 'maßgebend' : null),
    inhalt: (z) => (z.massgebend
      ? <StatusBadge zustand="info" text="maßgebend" klein />
      : <span className="schwach">—</span>),
  },
];

/**
 * Die Hydranten.
 *
 * Elf Stück. Interessant ist nicht die Liste, sondern welcher der
 * ungünstigste ist: An ihm hängt die Auslegung der Trennstation, und
 * 1.600 l/min müssen dort ankommen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DREI DINGE, DIE DER FACHPLANER AM 23.09.2026 GEMELDET HAT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. „— maßgebend" in der Spalte *Kennung*. Die Markierung klebte am
 *    fehlenden Wert und sah aus, als hieße der Hydrant so. Sie steht jetzt
 *    in einer eigenen Spalte an der Zeile, wo sie hingehört.
 *
 * 2. Die Spalte *Bauart* zeigte `ueberflur`. Das ist ein Datenbankschlüssel
 *    und keine Bauartbezeichnung.
 *
 * 3. Die Spalte *Befund* war leer, obwohl der Löschwasserlauf je Hydrant
 *    den Nachbarabstand nach DVGW W 331 und den Fließdruck kennt. Der
 *    Grund: Diese Tabelle las nur `aussen_hydranten.Befund` — das Feld der
 *    AUFNAHME. Der Befund der RECHNUNG steht in der Berechnungszeile, und
 *    die wurde hier nie geholt. Jetzt wird sie geholt und über die
 *    Elementkennung zugeordnet.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * „LIEGT NICHT VOR" IST ETWAS ANDERES ALS „—"
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Abgabe und Netzdruck trägt KEIN Hydrant (AU3 hat es gemessen). Eine
 * Spalte voller Gedankenstriche sieht aus, als hätte die Anzeige etwas
 * verloren. Der Hinweis unter der Tabelle sagt deshalb ausdrücklich, welche
 * Spalte an KEINER Zeile einen Wert hat — und er zählt es an den geladenen
 * Daten, statt es zu behaupten.
 */
function Hydranten({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const abruf = useAbruf(() => api.aussenHydranten(projektId), [api, projektId]);
  /** `undefined` = Abruf läuft, `null` = kein Löschwasserlauf. */
  const [nachweis, setNachweis] = useState<AussenNachweisHydranten | null | undefined>(undefined);

  useEffect(() => {
    // Ohne Löschwasserlauf gibt es keinen Nachweis - das ist kein Fehler
    // dieser Seite, und die Bestandsliste steht auch ohne ihn.
    api.aussenNachweisHydranten(projektId)
      .then(setNachweis)
      .catch(() => setNachweis(null));
  }, [api, projektId]);

  const stand = useBerichtsstand(api, projektId, 'loeschwasser');
  useAussenKopf({ ziele: [berichtZiel(api, projektId, 'loeschwasser', stand)] }, [api, projektId, stand]);

  const hydranten = abruf.daten ?? [];

  const zeilen: Hydrantenzeile[] = useMemo(() => {
    // Der Nachweis, nach Elementkennung. Die Bezeichnung taugt nicht als
    // Schlüssel: Sie ist im Lauf frei vergeben, die Elementkennung nicht.
    const jeElement = new Map<number, AussenNachweisHydrant>();
    for (const h of nachweis?.hydranten ?? [])
      if (h.revitElementId != null) jeElement.set(h.revitElementId, h);

    // Der ungünstigste ist der mit dem kleinsten Fließdruck. Er ist die
    // Auslegungsbasis - nicht der Mittelwert und nicht der erste in der Liste.
    const druecke = hydranten
      .map((h) => h.fliessdruckBar)
      .filter((d): d is number => d != null);
    const schwaechster = druecke.length > 0 ? Math.min(...druecke) : null;

    return hydranten.map((h) => {
      const n = h.revitElementId == null ? undefined : jeElement.get(h.revitElementId);
      const abstand = typeof n?.werte?.NachbarabstandM === 'number'
        ? n.werte.NachbarabstandM as number
        : typeof n?.werte?.nachbarabstandM === 'number'
          ? n.werte.nachbarabstandM as number
          : null;
      return { h, n, abstand, massgebend: schwaechster != null && h.fliessdruckBar === schwaechster };
    });
  }, [hydranten, nachweis]);

  // GEMESSEN an den geladenen Zeilen und nicht behauptet: Welche Spalte
  // trägt an KEINER Zeile einen Wert?
  const leereSpalten = hydranten.length === 0 ? [] : ([
    ['Kennung', (h: AussenHydrant) => h.kennung],
    ['DN', (h: AussenHydrant) => h.nennweite],
    ['Abgabe', (h: AussenHydrant) => h.abgabeLMin],
    ['Netzdruck', (h: AussenHydrant) => h.netzdruckBar],
    ['Fließdruck', (h: AussenHydrant) => h.fliessdruckBar],
  ] as const)
    .filter(([, lies]) => hydranten.every((h) => lies(h) == null))
    .map(([name]) => name);

  return (
    <ContentSection
      titel="Hydranten"
      beschreibung={'Auslegungsbasis nach DVGW W 405: 1.600 l/min über zwei Stunden, entnommen am ungünstigsten '
        + 'Hydranten — nicht an allen gleichzeitig. Das Netz ist eine Ringleitung; die Zeile mit dem kleinsten '
        + 'Fließdruck ist die maßgebende.'}
    >
      <DataTable<Hydrantenzeile>
        name="Überflurhydranten"
        spalten={HYDRANTENSPALTEN}
        zeilen={zeilen}
        schluessel={(z) => z.h.id}
        zeilenKlasse={(z) => (z.massgebend ? 'aus-zeile-warnung' : undefined)}
        laedt={abruf.laedt}
        fehler={abruf.fehler ?? undefined}
        beiErneutVersuchen={abruf.erneut}
        leer={<EmptyState klein titel="Kein Hydrant im Bestand."
          erklaerung="Überflurhydranten entstehen mit der Außenanlagenaufnahme („Modell lesen“)." />}
        dicht
      />

      {leereSpalten.length > 0 && (
        <p className="aus-hinweis" data-liegt-nicht-vor>
          <strong>Liegt nicht vor</strong> (an keiner der {hydranten.length}{' '}
          Zeilen ein Wert): {leereSpalten.join(', ')}. Das ist keine Lücke
          der Anzeige, sondern des Bestands — kein Hydrant im Modell trägt
          diese Angaben, und keine Rechnung hat sie bisher geschrieben.
        </p>
      )}

      {nachweis === null && (
        <p className="aus-hinweis">
          Für das Löschwasser wurde noch nicht gerechnet. Bewertung,
          Nachbarabstand und Befund stehen deshalb leer — unter
          „Berechnungen" auf „neu rechnen".
        </p>
      )}
    </ContentSection>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Versickerung
// ═══════════════════════════════════════════════════════════════════════════

type Einzugsflaeche = AussenVersickerungsbauwerk['einzugsflaechen'][number];

/** Eine Teilfläche mit ihrem Bauwerk - die Flächen ALLER Bauwerke in einer Tabelle, je Bauwerk gruppiert. */
interface Flaechenzeile { f: Einzugsflaeche; bauwerkId: string; bauwerk: string }

const bauwerksname = (v: AussenVersickerungsbauwerk) => v.typ ?? v.bezeichnung ?? bauartname(v.bauart);

const BAUWERKSPALTEN: Spalte<AussenVersickerungsbauwerk>[] = [
  { schluessel: 'bauart', titel: 'Bauart', immerSichtbar: true, wert: (v) => bauartname(v.bauart) },
  { schluessel: 'bauwerk', titel: 'Bauwerk', wert: (v) => bauwerksname(v) },
  {
    schluessel: 'volumenModellM3', titel: 'Volumen im Modell', einheit: 'm³', art: 'zahl', nachkomma: 2,
    fuss: { art: 'summe', schranke: true },
  },
  {
    schluessel: 'erforderlichM3', titel: 'erforderlich', einheit: 'm³', art: 'zahl', nachkomma: 2,
    fuss: { art: 'summe', schranke: true },
  },
  { schluessel: 'auM2', titel: 'A_u (reduziert)', einheit: 'm²', art: 'zahl', nachkomma: 1 },
  {
    schluessel: 'kfWert', titel: 'kf-Wert', einheit: 'm/s', ausrichtung: 'rechts',
    inhalt: (v) => kfText(v.kfWert),
  },
  {
    schluessel: 'flaechen', titel: 'Einzugsflächen', art: 'status',
    wert: (v) => v.einzugsflaechen.length,
    inhalt: (v) => (v.einzugsflaechen.length === 0
      ? <StatusBadge zustand="fehler" text="keine erfasst" klein
          titel="Sie steckt als Text im Typnamen und muss vom Planer als Zahl bestätigt werden." />
      : formatiereZahl(v.einzugsflaechen.length, 0)),
  },
  {
    schluessel: 'nachweisstand', titel: 'Nachweis', art: 'status',
    wert: (v) => nachweisstandname(v.nachweisstand),
    inhalt: (v) => (v.nachweisstand === 'nachgewiesen'
      ? <StatusBadge zustand="gut" text="nachgewiesen" klein />
      : <StatusBadge zustand="warnung" text={nachweisstandname(v.nachweisstand)} klein />),
  },
  { schluessel: 'befund', titel: 'Befund', inhalt: (v) => <span className="aus-fein">{v.befund ?? '—'}</span> },
];

const FLAECHENSPALTEN: Spalte<Flaechenzeile>[] = [
  { schluessel: 'bezeichnung', titel: 'Teilfläche', immerSichtbar: true, wert: ({ f }) => f.bezeichnung },
  { schluessel: 'art', titel: 'Art', wert: ({ f }) => f.art },
  {
    schluessel: 'flaeche', titel: 'A', einheit: 'm²', art: 'zahl', nachkomma: 0,
    wert: ({ f }) => f.flaecheM2, fuss: { art: 'summe', schranke: true },
  },
  { schluessel: 'anteil', titel: 'Anteil', einheit: '%', art: 'zahl', nachkomma: 0, wert: ({ f }) => f.anteilProzent },
  { schluessel: 'psi', titel: 'ψ', art: 'zahl', nachkomma: 2, wert: ({ f }) => f.abflussbeiwert },
  {
    schluessel: 'beitrag', titel: 'Beitrag', einheit: 'm²', art: 'zahl', nachkomma: 0,
    wert: ({ f }) => f.beitragM2, fuss: { art: 'summe', schranke: true },
  },
  {
    schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
    wert: ({ f }) => (f.bestaetigt ? 'bestätigt' : `${herkunftname(f.herkunftFlaeche)} unbestätigt`),
    inhalt: ({ f }) => (f.bestaetigt
      ? <StatusBadge zustand="gut" text="bestätigt" klein />
      : <StatusBadge zustand="warnung" text={`${herkunftname(f.herkunftFlaeche)} — unbestätigt`} klein />),
  },
];

/**
 * Versickerung und Rückhaltung: die Bauwerke in einer Tabelle, ihre
 * Teilflächen in einer zweiten, je Bauwerk gruppiert - die Zwischensumme der
 * Beiträge je Gruppe ist das A_u des Bauwerks.
 */
function Versickerung({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const abruf = useAbruf(() => api.aussenVersickerung(projektId), [api, projektId]);
  const stand = useBerichtsstand(api, projektId, 'versickerung');
  useAussenKopf({ ziele: [berichtZiel(api, projektId, 'versickerung', stand)] }, [api, projektId, stand]);

  const bauwerke = abruf.daten ?? [];
  const flaechen: Flaechenzeile[] = bauwerke.flatMap((v) => v.einzugsflaechen.map((f) => ({
    f, bauwerkId: v.id, bauwerk: bauwerksname(v),
  })));

  return (
    <>
      <ContentSection
        titel="Versickerung und Rückhaltung"
        beschreibung={'Das Volumen ist modelliert, die Bemessung nicht. A_u ist die reduzierte abflusswirksame '
          + 'Fläche: Σ(A · Anteil · ψ). Wo sie fehlt, gibt es keine — eine Summe aus lauter Unbekannten ist keine '
          + 'Null, sondern keine Aussage.'}
      >
        <DataTable<AussenVersickerungsbauwerk>
          name="Bauwerke"
          spalten={BAUWERKSPALTEN}
          zeilen={bauwerke}
          schluessel={(v) => v.id}
          laedt={abruf.laedt}
          fehler={abruf.fehler ?? undefined}
          beiErneutVersuchen={abruf.erneut}
          leer={<EmptyState klein titel="Kein Versickerungsbauwerk im Bestand."
            erklaerung="Rigolen, Mulden und Rückhaltungen entstehen mit der Außenanlagenaufnahme („Modell lesen“)." />}
          dicht
        />
      </ContentSection>

      {abruf.daten && (
        <ContentSection
          titel="Einzugsflächen"
          beschreibung={'Je Bauwerk die Teilflächen mit Anteil und Abflussbeiwert. Eine unbestätigte Fläche ist aus '
            + 'dem Typnamen gelesen und muss vom Planer bestätigt werden.'}
        >
          <DataTable<Flaechenzeile>
            name="Einzugsflächen je Bauwerk"
            spalten={FLAECHENSPALTEN}
            zeilen={flaechen}
            schluessel={({ f }) => f.id}
            gruppierung={{
              nach: (z) => z.bauwerkId,
              titel: (_k, zeilen) => zeilen[0]?.bauwerk ?? '—',
            }}
            zeilenKlasse={({ f }) => (f.bestaetigt ? undefined : 'aus-zeile-warnung')}
            leer={<EmptyState klein titel="Keine Einzugsfläche erfasst."
              erklaerung="Sie steckt als Text im Typnamen und muss vom Planer als Zahl bestätigt werden." />}
            ausfuhr
            ausfuhrDateiname="aussen-einzugsflaechen"
            dicht
          />
        </ContentSection>
      )}
    </>
  );
}
